import { useEffect, useMemo, useState } from "react";

import { Dialog } from "../Dialog.jsx";
import { FleetOrderMonitoringCard } from "./FleetOrderMonitoringCard.jsx";
import {
  buildMonitoringFileName,
  buildMonitoringPages,
  buildWhatsAppCaption,
  canShareFleetOrderMonitoring,
  generateMonitoringPagePng,
} from "../../utils/fleet-order-monitoring.js";

const FEEDBACK_DURATION_MS = 2400;

function getErrorMessage(error, fallback = "Aksi tidak dapat diproses.") {
  return error?.message || fallback;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function createShareFile(blob, filename) {
  if (typeof File === "undefined" || !blob) return null;

  return new File([blob], filename, {
    type: "image/png",
  });
}

function canUseClipboardImage() {
  return Boolean(
    typeof window !== "undefined" &&
      window.isSecureContext &&
      navigator?.clipboard?.write &&
      window.ClipboardItem,
  );
}

function canShareFiles(files) {
  return Boolean(
    files.length > 0 &&
      typeof navigator !== "undefined" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({
        files,
      }),
  );
}


function isMobileNativeShareDevice() {
  if (typeof navigator === "undefined") return false;

  const userAgent = String(navigator.userAgent || "");
  const mobileUserAgent = /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);
  const iPadDesktopMode =
    navigator.platform === "MacIntel" &&
    Number(navigator.maxTouchPoints || 0) > 1;

  return mobileUserAgent || iPadDesktopMode;
}

export function FleetOrderShareModal({ fleetOrder, onClose }) {
  const availability = useMemo(() => canShareFleetOrderMonitoring(fleetOrder), [fleetOrder]);
  const pages = useMemo(
    () => (availability.allowed ? buildMonitoringPages(fleetOrder) : []),
    [availability.allowed, fleetOrder],
  );
  const caption = useMemo(
    () => (availability.allowed ? buildWhatsAppCaption(fleetOrder) : ""),
    [availability.allowed, fleetOrder],
  );

  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [pageBlobs, setPageBlobs] = useState([]);
  const [pageUrls, setPageUrls] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [renderError, setRenderError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!feedback) return undefined;

    const timer = window.setTimeout(() => setFeedback(""), FEEDBACK_DURATION_MS);

    return () => window.clearTimeout(timer);
  }, [feedback]);

  useEffect(() => {
    let cancelled = false;
    const createdUrls = [];

    setSelectedPageIndex(0);
    setPageBlobs([]);
    setPageUrls([]);
    setRenderError("");

    if (!availability.allowed || pages.length === 0) {
      setGenerating(false);
      return undefined;
    }

    async function renderPages() {
      setGenerating(true);

      try {
        const nextBlobs = [];
        const nextUrls = [];

        for (const page of pages) {
          const blob = await generateMonitoringPagePng(page, fleetOrder);
          const url = URL.createObjectURL(blob);

          createdUrls.push(url);
          nextBlobs.push(blob);
          nextUrls.push(url);
        }

        if (!cancelled) {
          setPageBlobs(nextBlobs);
          setPageUrls(nextUrls);
        }
      } catch (error) {
        if (!cancelled) {
          setRenderError(getErrorMessage(error, "PNG monitoring gagal dibuat."));
        }
      } finally {
        if (!cancelled) {
          setGenerating(false);
        }
      }
    }

    renderPages();

    return () => {
      cancelled = true;
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [availability.allowed, fleetOrder, pages, retryKey]);

  const currentPage = pages[selectedPageIndex] || pages[0] || null;
  const currentBlob = pageBlobs[selectedPageIndex] || null;
  const currentImageUrl = pageUrls[selectedPageIndex] || "";
  const currentFilename = currentPage
    ? buildMonitoringFileName(fleetOrder, selectedPageIndex, currentPage.pageCount)
    : "";
  const currentShareFile = currentBlob
    ? createShareFile(currentBlob, currentFilename)
    : null;
  const canShareCurrentPage = canShareFiles(
    currentShareFile ? [currentShareFile] : [],
  );
  /*
   * Windows can report file-sharing support but still open an unusable
   * system share surface. Native file sharing is therefore offered only on
   * mobile/iPad-class devices. Desktop keeps the reliable clipboard,
   * download, and WhatsApp actions.
   */
  const showNativeShare =
    isMobileNativeShareDevice() && canShareCurrentPage;
  const isMultiPage = pages.length > 1;

  async function copyCaption() {
    if (!navigator?.clipboard?.writeText) {
      setFeedback("Browser ini belum mendukung salin caption otomatis.");
      return;
    }

    try {
      await navigator.clipboard.writeText(caption);
      setFeedback("Caption disalin.");
    } catch {
      setFeedback("Caption gagal disalin. Periksa izin clipboard browser.");
    }
  }

  async function copyImage() {
    if (!currentBlob) {
      setFeedback("Gambar monitoring belum siap.");
      return;
    }

    if (!canUseClipboardImage()) {
      setFeedback("Browser ini belum mendukung salin gambar. Gunakan Unduh PNG.");
      return;
    }

    try {
      const ClipboardItemCtor = window.ClipboardItem;

      await navigator.clipboard.write([
        new ClipboardItemCtor({
          "image/png": currentBlob,
        }),
      ]);

      setFeedback(
        isMultiPage
          ? `Gambar bagian ${selectedPageIndex + 1} disalin.`
          : "Gambar monitoring disalin.",
      );
    } catch {
      setFeedback("Gambar gagal disalin. Gunakan Unduh PNG.");
    }
  }

  function downloadCurrentPage() {
    if (!currentBlob) {
      setFeedback("Gambar monitoring belum siap.");
      return;
    }

    downloadBlob(currentBlob, currentFilename);
    setFeedback(
      isMultiPage
        ? `PNG bagian ${selectedPageIndex + 1} diunduh.`
        : "PNG monitoring diunduh.",
    );
  }

  function downloadAllPages() {
    if (pageBlobs.length !== pages.length) {
      setFeedback("Semua gambar monitoring belum siap.");
      return;
    }

    pageBlobs.forEach((blob, index) => {
      downloadBlob(blob, buildMonitoringFileName(fleetOrder, index, pages.length));
    });

    setFeedback("Semua PNG monitoring diunduh.");
  }

  function openWhatsApp() {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(caption)}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  async function shareNative() {
    if (!showNativeShare || !currentShareFile) return;

    try {
      await navigator.share({
        title: `Monitoring ${fleetOrder?.foNumber || "Fleet Order"}`,
        text: caption,
        files: [currentShareFile],
      });

      setFeedback(
        isMultiPage
          ? `Monitoring bagian ${selectedPageIndex + 1} dibagikan.`
          : "Monitoring dibagikan.",
      );
    } catch (error) {
      if (error?.name === "AbortError") return;

      setFeedback(
        "Berbagi melalui perangkat gagal. Gunakan Salin Gambar, Unduh PNG, atau Buka WhatsApp.",
      );
    }
  }

  return (
    <Dialog className="dialog-card--fleet-monitoring-share" onClose={onClose} title="Bagikan Monitoring FO">
      {!availability.allowed ? (
        <div className="dialog-card__body fleet-monitoring-share-state">
          <strong>Monitoring belum dapat dibuat.</strong>
          <p>{availability.reason}</p>
        </div>
      ) : (
        <>
          <div className="fleet-monitoring-share">
            <section className="fleet-monitoring-share__preview" aria-label="Preview gambar monitoring">
              <div className="fleet-monitoring-share__header">
                <div>
                  <span>Preview PNG</span>
                  <strong>{fleetOrder?.foNumber}</strong>
                </div>

                {isMultiPage && (
                  <div className="fleet-monitoring-page-selector" aria-label="Pilih bagian monitoring">
                    {pages.map((page, index) => (
                      <button
                        aria-label={`Tampilkan bagian ${page.pageNumber} dari ${page.pageCount}`}
                        className={index === selectedPageIndex ? "is-active" : ""}
                        disabled={generating && !pageUrls[index]}
                        key={page.pageNumber}
                        onClick={() => setSelectedPageIndex(index)}
                        type="button"
                      >
                        {page.pageNumber}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {renderError && (
                <div className="error-banner" role="alert">
                  <span>{renderError}</span>
                  <button
                    className="secondary-button"
                    onClick={() => setRetryKey((value) => value + 1)}
                    type="button"
                  >
                    Coba Lagi
                  </button>
                </div>
              )}

              <FleetOrderMonitoringCard
                alt={`Monitoring ${fleetOrder?.foNumber || "Fleet Order"} bagian ${
                  selectedPageIndex + 1
                } dari ${pages.length}`}
                imageUrl={currentImageUrl}
                loading={generating && !currentImageUrl}
                pageLabel={
                  isMultiPage
                    ? `Bagian ${selectedPageIndex + 1} dari ${pages.length}`
                    : "1 halaman monitoring"
                }
              />

              <div className="fleet-monitoring-share__inline-actions">
                <button
                  className="secondary-button"
                  disabled={!currentBlob}
                  onClick={copyImage}
                  type="button"
                >
                  Salin Gambar
                </button>

                <button
                  className="secondary-button"
                  disabled={!currentBlob}
                  onClick={downloadCurrentPage}
                  type="button"
                >
                  Unduh PNG
                </button>

                {isMultiPage && (
                  <button
                    className="secondary-button"
                    disabled={pageBlobs.length !== pages.length}
                    onClick={downloadAllPages}
                    type="button"
                  >
                    Unduh Semua
                  </button>
                )}
              </div>
            </section>

            <section className="fleet-monitoring-share__caption">
              <div className="fleet-monitoring-share__header">
                <div>
                  <span>Caption WhatsApp</span>
                  <strong>Siap disalin</strong>
                </div>
              </div>

              <label className="fleet-monitoring-caption">
                <span>Caption monitoring</span>
                <textarea readOnly value={caption} />
              </label>

              <button className="secondary-button" onClick={copyCaption} type="button">
                Salin Caption
              </button>

              <div className="fleet-monitoring-share__helper">
                WhatsApp akan dibuka dengan caption. Tempel atau lampirkan gambar monitoring
                sebelum mengirim.
              </div>

              {isMultiPage && (
                <div className="fleet-monitoring-share__helper">
                  Fleet Order ini memiliki {pages.length} gambar. Salin atau unduh setiap bagian
                  agar semua stop ikut terbagi.
                </div>
              )}
            </section>
          </div>

          <footer className="dialog-card__actions fleet-monitoring-share__actions">
            <span className="fleet-monitoring-feedback" aria-live="polite">
              {generating
                ? "Menyiapkan monitoring Fleet Order…"
                : feedback || "PNG 1080 × 1350 siap untuk WhatsApp."}
            </span>

            <button className="secondary-button" onClick={onClose} type="button">
              Tutup
            </button>

            <button className="secondary-button" onClick={openWhatsApp} type="button">
              Buka WhatsApp
            </button>

            {showNativeShare && (
              <button
                className="primary-button"
                disabled={!currentBlob}
                onClick={shareNative}
                type="button"
              >
                Bagikan
              </button>
            )}
          </footer>
        </>
      )}
    </Dialog>
  );
}
